import { getUniqueId } from "@helpers/general";
import {
    createDefaultKitchenTicket,
    createDefaultCustomerTicket,
    createDefaultModelKitchenTicket,
    createDefaultGeneralSettings,
    createDefaultDiversSettings,
} from "@helpers/printerDefaults";

export type PrinterCommonFields = {
    designation: string;
    mark: string;
    remark: string;
    printerType: string;
    speed: string | number;
    printerPath: string;
    ip: string;
    ipPortNumber: string;
    fontSize: string | number;
    ticketWidth: string | number;
    portSeries: string;
    printerCode: any;
    openDrawer: boolean;
};

/**
 * Builds the dataPrint object when ticket type changes during edit.
 * Used when switching between Customer (01) and Production/Sticker/Voucher (02/03/04).
 */
export function buildTicketTypeChangePrint(
    salesModes: any[],
    isKitchenActive: boolean,
    isCustomerActive: boolean
): { Kitchen_ticket: any; customer_ticket: any } {
    return {
        Kitchen_ticket: createDefaultKitchenTicket(salesModes, isKitchenActive),
        customer_ticket: createDefaultCustomerTicket(
            salesModes,
            isCustomerActive
        ),
    };
}

/**
 * Builds the savedData payload for adding a new printer.
 */
export function buildPrinterAddPayload(params: {
    userId: number;
    shopId: string;
    franchiseId: string;
    operatorId: string;
    uuidUser: string | null;
    fields: PrinterCommonFields;
    originTicket: any;
    salesModes: any[];
}): any {
    const {
        userId,
        shopId,
        franchiseId,
        operatorId,
        uuidUser,
        fields,
        originTicket,
        salesModes,
    } = params;
    const isKitchenActive =
        originTicket.value === "02" || originTicket.value === "04";
    const isCustomerActive = originTicket.value === "01";

    return {
        userId,
        shopId,
        franchiseId,
        operatorId,
        uuidUser,
        data: [
            {
                id: getUniqueId(10),
                designation: fields.designation.trim(),
                mark: fields.mark,
                note: fields.remark,
                shopId,
                type: fields.printerType,
                speed: fields.printerType !== "ip" ? fields.speed : "",
                path: fields.printerPath,
                ip: fields.ip,
                port: fields.ipPortNumber,
                font_size: fields.fontSize,
                edit_date: new Date(),
                add_date: new Date(),
                ticket_width: fields.ticketWidth,
                port_series: fields.portSeries,
                isArchived: false,
                code: fields.printerCode,
                tickets: originTicket,
                open_drawer: fields.openDrawer,
                Kitchen_ticket: createDefaultKitchenTicket(
                    salesModes,
                    isKitchenActive
                ),
                customer_ticket: createDefaultCustomerTicket(
                    salesModes,
                    isCustomerActive
                ),
                modelKitchenTicket: createDefaultModelKitchenTicket(),
                general: createDefaultGeneralSettings(true),
                divers: createDefaultDiversSettings(true),
            },
        ],
    };
}

/**
 * Builds the savedDataApplication payload for editing a printer
 * (preserves existing ticket configuration from peripheral/editedData).
 */
export function buildPrinterEditApplicationPayload(params: {
    userId: number;
    shopId: string;
    franchiseId: string;
    operatorId: string;
    uuidUser: string | null;
    editedData: any;
    fields: PrinterCommonFields;
    originTicket: any;
    peripheral: any;
}): any {
    const {
        userId,
        shopId,
        franchiseId,
        operatorId,
        uuidUser,
        editedData,
        fields,
        originTicket,
        peripheral,
    } = params;

    return {
        userId,
        shopId,
        franchiseId,
        operatorId,
        uuidUser,
        data: [
            {
                id: editedData["Id"],
                designation: fields.designation,
                mark: fields.mark,
                note: fields.remark,
                shopId,
                type: fields.printerType,
                speed: fields.speed,
                path: fields.printerPath,
                ip: fields.ip,
                port: fields.ipPortNumber,
                font_size: fields.fontSize,
                edit_date: new Date(),
                add_date: "",
                ticket_width: fields.ticketWidth,
                port_series: fields.portSeries,
                isArchived: false,
                code: fields.printerCode,
                tickets: originTicket,
                open_drawer: fields.openDrawer,
                Kitchen_ticket:
                    peripheral.Kitchen_ticket ?? editedData.Kitchen_ticket,
                customer_ticket:
                    peripheral.customer_ticket ?? editedData.customer_ticket,
                modelKitchenTicket:
                    peripheral.modelKitchenTicket ??
                    editedData.modelKitchenTicket,
                general: peripheral.general ?? editedData.general,
                divers: peripheral.divers ?? editedData.divers,
            },
        ],
    };
}

/**
 * Builds the main savedData payload for editing a printer.
 */
export function buildPrinterEditPayload(params: {
    userId: number;
    shopId: string;
    franchiseId: string;
    operatorId: string;
    uuidUser: string | null;
    editedData: any;
    fields: PrinterCommonFields;
    originTicket: any;
    isSameTicketValue: boolean;
    dataPrint: any;
    application: any;
}): any {
    const {
        userId,
        shopId,
        franchiseId,
        operatorId,
        uuidUser,
        editedData,
        fields,
        originTicket,
        isSameTicketValue,
        dataPrint,
        application,
    } = params;

    return {
        userId,
        shopId,
        franchiseId,
        operatorId,
        uuidUser,
        peripheralType: "printer",
        data: [
            {
                id: editedData["Id"],
                designation: fields.designation,
                mark: fields.mark,
                note: fields.remark,
                shopId,
                type: fields.printerType,
                speed: fields.speed,
                path: fields.printerPath,
                ip: fields.ip,
                port: fields.ipPortNumber,
                font_size: fields.fontSize,
                edit_date: new Date(),
                add_date: "",
                ticket_width: fields.ticketWidth,
                port_series: fields.portSeries,
                isArchived: false,
                code: fields.printerCode,
                tickets: originTicket,
                open_drawer: fields.openDrawer,
                Kitchen_ticket: isSameTicketValue
                    ? editedData.Kitchen_ticket
                    : dataPrint?.Kitchen_ticket,
                customer_ticket: isSameTicketValue
                    ? editedData.customer_ticket
                    : dataPrint?.customer_ticket,
                modelKitchenTicket: isSameTicketValue
                    ? editedData.modelKitchenTicket
                    : editedData.modelKitchenTicket,
                general: isSameTicketValue
                    ? editedData.general
                    : editedData.general,
                divers: isSameTicketValue
                    ? editedData.divers
                    : editedData.divers,
            },
        ],
        application,
    };
}
